import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, Edit, Trash2, ChevronUp, ChevronDown, Save, Briefcase } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { Button } from '../../components';
import { experiencesService } from '../../services';

const HeaderActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const ExperienceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ExperienceRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem 1.25rem;
  background: ${props => props.theme.colors.background.secondary};
  border: 1px solid ${props => props.theme.colors.border?.light || 'rgba(0,0,0,0.1)'};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

const OrderControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  padding-top: 2px;

  button {
    background: none;
    border: 1px solid ${props => props.theme.colors.border?.light || 'rgba(0,0,0,0.15)'};
    border-radius: 4px;
    cursor: pointer;
    padding: 2px 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${props => props.theme.colors.text.secondary};
    transition: all 0.15s ease;

    &:hover:not(:disabled) {
      background: ${props => props.theme.colors.primaryLight};
      color: ${props => props.theme.colors.primary};
      border-color: ${props => props.theme.colors.primary};
    }

    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  }
`;

const ExperienceInfo = styled.div`
  flex: 1;
  min-width: 0;

  .role {
    font-weight: 600;
    color: ${props => props.theme.colors.text.primary};
    font-size: ${props => props.theme.typography.body};
  }

  .company {
    color: ${props => props.theme.colors.text.secondary};
    font-size: ${props => props.theme.typography.small};
    margin-top: 2px;
  }

  .period {
    display: inline-block;
    margin-top: 4px;
    font-size: 11px;
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    background: ${props => props.theme.colors.primaryLight};
    padding: 2px 8px;
    border-radius: 20px;
  }

  .subtitle {
    color: ${props => props.theme.colors.text.tertiary};
    font-size: ${props => props.theme.typography.small};
    font-style: italic;
    margin-top: 4px;
  }
`;

const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
`;

const SaveBar = styled.div`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  z-index: 100;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: ${props => props.theme.colors.text.secondary};

  h3 {
    color: ${props => props.theme.colors.text.primary};
    margin-bottom: 1rem;
  }

  p {
    margin-bottom: 2rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${props => props.theme.colors.text.secondary};
`;

export const ExperiencesAdmin = () => {
  const [experiences, setExperiences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    fetchExperiences();
  }, []);

  const fetchExperiences = async () => {
    try {
      const data = await experiencesService.getAllExperiences();
      setExperiences(data);
    } catch (error) {
      console.error('Error fetching experiences:', error);
    } finally {
      setLoading(false);
    }
  };

  const move = (index, direction) => {
    const next = [...experiences];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= next.length) return;
    [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
    setExperiences(next);
    setIsDirty(true);
  };

  const handleSaveOrder = async () => {
    setSaving(true);
    try {
      await experiencesService.updateExperiencesOrder(experiences.map(e => e.id));
      setIsDirty(false);
    } catch (error) {
      console.error('Error saving order:', error);
      alert('Failed to save order.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this experience?')) {
      try {
        await experiencesService.deleteExperience(id);
        setExperiences(experiences.filter(e => e.id !== id));
        setIsDirty(true);
      } catch (error) {
        console.error('Error deleting experience:', error);
        alert('Failed to delete experience.');
      }
    }
  };

  if (loading) {
    return (
      <AdminLayout title="Experiences">
        <LoadingContainer><p>Loading experiences...</p></LoadingContainer>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Experiences">
      <HeaderActions>
        <h2 style={{ margin: 0, color: 'var(--color-text-primary)' }}>
          All Experiences ({experiences.length})
        </h2>
        <Button as={Link} to="/admin/experiences/new">
          <Plus size={18} />
          New Experience
        </Button>
      </HeaderActions>

      {experiences.length === 0 ? (
        <EmptyState>
          <h3>No experiences yet</h3>
          <p>Add your work history to showcase your career journey.</p>
          <Button as={Link} to="/admin/experiences/new" size="lg">
            <Plus size={20} />
            Add First Experience
          </Button>
        </EmptyState>
      ) : (
        <ExperienceList>
          {experiences.map((exp, index) => (
            <ExperienceRow key={exp.id}>
              <OrderControls>
                <button onClick={() => move(index, -1)} disabled={index === 0} title="Move up">
                  <ChevronUp size={14} />
                </button>
                <button onClick={() => move(index, 1)} disabled={index === experiences.length - 1} title="Move down">
                  <ChevronDown size={14} />
                </button>
              </OrderControls>

              <span style={{ fontSize: '13px', color: 'var(--color-text-tertiary)', width: '20px', textAlign: 'right', flexShrink: 0, paddingTop: '2px' }}>
                {index + 1}
              </span>

              <Briefcase size={18} style={{ flexShrink: 0, color: 'var(--color-text-tertiary)', marginTop: '2px' }} />

              <ExperienceInfo>
                <div className="role">{exp.role}</div>
                <div className="company">{exp.company}</div>
                {exp.period && <span className="period">{exp.period}</span>}
                {exp.subtitle && <div className="subtitle">{exp.subtitle}</div>}
              </ExperienceInfo>

              <RowActions>
                <Button
                  as={Link}
                  to={`/admin/experiences/${exp.id}/edit`}
                  variant="outline"
                  size="sm"
                >
                  <Edit size={14} />
                  Edit
                </Button>
                <Button
                  onClick={() => handleDelete(exp.id)}
                  variant="ghost"
                  size="sm"
                  style={{ color: 'var(--color-error)' }}
                >
                  <Trash2 size={14} />
                </Button>
              </RowActions>
            </ExperienceRow>
          ))}
        </ExperienceList>
      )}

      {isDirty && (
        <SaveBar>
          <Button onClick={handleSaveOrder} disabled={saving} size="lg">
            <Save size={18} />
            {saving ? 'Saving...' : 'Save Order'}
          </Button>
        </SaveBar>
      )}
    </AdminLayout>
  );
};
